import React, { FC, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { LinkContainer, SideMenuLink, SvgIcon, Tooltip } from '@portal/common/components';
import { styled } from '@portal/common/theme';
import { ResourceList } from '@portal/common/types';

import { FAVORITE_CONTENT_PATH, MY_LISTS_PATH, PUBLIC_LISTS_PATH } from '../../../router/paths';
import { getSessionAccountId } from '../../../store/session/selectors';

type Props = {
    list: Partial<ResourceList>;
    isPublic?: boolean;
    resourceCount?: number;
};

const SidenavNavLink = styled(SideMenuLink)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    height: 'auto',
    color: theme.color.gray40,
    ':hover, :active, &.active': {
        color: theme.color.green2,
        svg: {
            color: theme.color.green2,
        },
    },
}));

const StyledIcon = styled(SvgIcon)({
    marginRight: 10,
    verticalAlign: 'top',
});

const LinkName = styled('div')({
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
});

const ListItem: FC<Props> = ({ list, resourceCount, isPublic, ...props }) => {
    const nameRef = useRef(null);
    const memberId = useSelector(getSessionAccountId);
    const [isNameTruncated, setIsNameTruncated] = useState(false);
    const path = `${FAVORITE_CONTENT_PATH}${isPublic ? PUBLIC_LISTS_PATH : MY_LISTS_PATH}`;
    const count = list.resource_ids ? list.resource_ids?.length : 0;
    const isCreator = list.creator && list.creator.id !== memberId;
    const icon =
        !isPublic && list.is_public && isCreator ? (
            <StyledIcon size="medium" src="/icons/ic-team.svg" />
        ) : null;
    const tooltipText = `${list.name}\n${list.description ? list.description : ''}`.trim();

    useEffect(() => {
        nameRef?.current &&
            setIsNameTruncated(nameRef.current.offsetWidth < nameRef.current.scrollWidth);
    }, []);

    return (
        <Tooltip
            key={`tooltip-${list.id}`}
            title={tooltipText}
            trigger={isNameTruncated || list.description ? 'hover' : 'none'}
        >
            <div>
                <LinkContainer key={list.id} to={path + `/${list.id}`}>
                    <SidenavNavLink {...props}>
                        <LinkName innerRef={nameRef}>
                            {icon}
                            {list.name}
                        </LinkName>
                        <div>{resourceCount ?? count}</div>
                    </SidenavNavLink>
                </LinkContainer>
            </div>
        </Tooltip>
    );
};

export default ListItem;
